import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Calculator, UserPlus, FolderOpen, Users, FileText, LogOut, Settings, Shield, DollarSign, Lightbulb, X, Package, HelpCircle, SearchIcon, Star, Mail, Film } from "lucide-react";
import { DISABLED_MODULES } from "@/config/disabledModules";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [pendingUsersCount, setPendingUsersCount] = useState(0);
  const [showFeatureForm, setShowFeatureForm] = useState(false);
  const [featureForm, setFeatureForm] = useState({
    name: '',
    phone: '',
    company: '',
    suggestion: ''
  });
  const [featureSubmitting, setFeatureSubmitting] = useState(false);
  const [featureSuccess, setFeatureSuccess] = useState(false);
  const [featureError, setFeatureError] = useState('');

  useEffect(() => {
    if (user && (user.role === 'admin' || user.role === 'distributor_admin' || user.role === 'zkteco_admin')) {
      fetchPendingUsersCount();
    }
  }, [user]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchPendingUsersCount = async () => {
    try {
      const response = await axios.get(`${API}/admin/users/pending`, { headers: getAuthHeaders() });
      setPendingUsersCount(response.data.length);
    } catch (err) {
      console.error("Failed to fetch pending users count:", err);
    }
  };

  const handleFeatureSubmit = async (e) => {
    e.preventDefault();
    setFeatureSubmitting(true);
    setFeatureError('');
    setFeatureSuccess(false);

    try {
      await axios.post(
        `${API}/feature-suggestion`,
        {
          name: featureForm.name,
          phone: featureForm.phone,
          company: featureForm.company,
          suggestion: featureForm.suggestion,
          user_email: user?.email
        },
        { headers: getAuthHeaders() }
      );
      setFeatureSuccess(true);
      setFeatureForm({ name: '', phone: '', company: '', suggestion: '' });
      setTimeout(() => {
        setShowFeatureForm(false);
        setFeatureSuccess(false);
      }, 2000);
    } catch (err) {
      setFeatureError(err.response?.data?.detail || 'Failed to submit suggestion');
    } finally {
      setFeatureSubmitting(false);
    }
  };

  // Check if user can see Amazon module
  const canSeeAmazon = !DISABLED_MODULES.amazon && user && (user.role === 'admin' || user.role === 'zkteco_admin' || user.role === 'zkteco');

  // Base dashboard items for all users
  const baseDashboardItems = [
    {
      id: 'pedestrian-entrance-control',
      title: 'Pedestrian Entrance Control',
      subtitle: '(Half Height Turnstiles, Speedlanes and Full Height Turnstiles)',
      icon: Calculator,
      color: 'from-green-500 to-green-600',
      hoverColor: 'hover:from-green-600 hover:to-green-700',
      navigate: 'pedestrian-entrance-control'
    },
    {
      id: 'access-control',
      title: 'Access Control and Visitor Management',
      subtitle: '(Atlas Access, Bio CVSecurity And Visitor Management Solutions)',
      icon: Calculator,
      color: 'from-blue-500 to-blue-600',
      hoverColor: 'hover:from-blue-600 hover:to-blue-700',
      navigate: 'access-control'
    },
    {
      id: 'parking-barrier-systems',
      title: 'Parking Barrier Systems',
      icon: Calculator,
      color: 'from-orange-600 to-yellow-600',
      hoverColor: 'hover:from-orange-700 hover:to-yellow-700',
      navigate: 'parking-barrier-systems'
    },
    ...(!DISABLED_MODULES.presentations ? [{
      id: 'presentation-resource',
      title: 'Presentation Resource',
      icon: Film,
      color: 'from-violet-500 to-purple-600',
      hoverColor: 'hover:from-violet-600 hover:to-purple-700',
      navigate: 'presentation-resource'
    }] : []),
    // Amazon module - only visible to admin, zkteco_admin, zkteco
    ...(canSeeAmazon ? [{
      id: 'amazon',
      title: 'Amazon',
      subtitle: '(Hardening Project Portal)',
      icon: Package,
      color: 'from-orange-500 to-amber-600',
      hoverColor: 'hover:from-orange-600 hover:to-amber-700',
      navigate: 'amazon/dashboard'
    }] : []),
    {
      id: 'parts-lookup',
      title: 'Parts Lookup',
      subtitle: '(Search SKU Pricing)',
      icon: SearchIcon,
      color: 'from-teal-500 to-emerald-600',
      hoverColor: 'hover:from-teal-600 hover:to-emerald-700',
      navigate: 'parts-lookup'
    },
    {
      id: 'invite-team',
      title: 'Invite Team/Installers',
      icon: UserPlus,
      color: 'from-green-500 to-green-600',
      hoverColor: 'hover:from-green-600 hover:to-green-700',
      navigate: 'invite-team'
    },
    {
      id: 'saved-quotes',
      title: 'Saved Quotes',
      icon: FolderOpen,
      color: 'from-green-500 to-green-600',
      hoverColor: 'hover:from-green-600 hover:to-green-700',
      navigate: 'saved-quotes'
    },
    {
      id: 'help',
      title: 'How to Use',
      subtitle: '(Guide & Help)',
      icon: HelpCircle,
      color: 'from-cyan-500 to-blue-600',
      hoverColor: 'hover:from-cyan-600 hover:to-blue-700',
      navigate: 'help'
    }
  ];

  // Admin-only items
  const adminItems = [
    {
      id: 'admin',
      title: 'Admin',
      icon: Users,
      color: 'from-purple-500 to-purple-600',
      hoverColor: 'hover:from-purple-600 hover:to-purple-700',
      navigate: 'admin',
      badge: pendingUsersCount > 0 ? pendingUsersCount : null
    },
    {
      id: 'reports',
      title: 'Reports',
      icon: FileText,
      color: 'from-blue-500 to-blue-600',
      hoverColor: 'hover:from-blue-600 hover:to-blue-700',
      navigate: 'reports'
    }
  ];

  // Shared tools for admin, zkteco_admin and zkteco users (view-only access)
  const sharedToolsItems = user && (user.role === 'admin' || user.role === 'zkteco_admin' || user.role === 'zkteco') ? [
    {
      id: 'datasheet-management',
      title: 'Datasheet Management',
      icon: FileText,
      color: 'from-purple-500 to-indigo-600',
      hoverColor: 'hover:from-purple-600 hover:to-indigo-700',
      navigate: 'datasheet-management'
    }
  ] : [];

  // Admin and ZKTeco Admin tools (sensitive pricing/discount management)
  const adminOnlyToolsItems = user && (user.role === 'admin' || user.role === 'zkteco_admin') ? [
    {
      id: 'distributor-discounts',
      title: 'Distributor Discounts',
      icon: Calculator,
      color: 'from-blue-500 to-cyan-600',
      hoverColor: 'hover:from-blue-600 hover:to-cyan-700',
      navigate: 'distributor-discounts'
    },
    {
      id: 'product-discount-caps',
      title: 'Product Discount Caps',
      icon: Shield,
      color: 'from-orange-500 to-red-600',
      hoverColor: 'hover:from-orange-600 hover:to-red-700',
      navigate: 'product-discount-caps'
    },
    {
      id: 'pricing-management',
      title: 'Pricing Management',
      icon: DollarSign,
      color: 'from-emerald-500 to-teal-600',
      hoverColor: 'hover:from-emerald-600 hover:to-teal-700',
      navigate: 'pricing-management'
    },
    {
      id: 'pricelist-converter',
      title: 'Price List Converter',
      icon: FileText,
      color: 'from-slate-600 to-slate-700',
      hoverColor: 'hover:from-slate-700 hover:to-slate-800',
      navigate: 'pricelist-converter'
    },
    {
      id: 'feedback-responses',
      title: 'Feedback Responses',
      icon: Star,
      color: 'from-amber-500 to-orange-600',
      hoverColor: 'hover:from-amber-600 hover:to-orange-700',
      navigate: 'feedback-responses'
    }
  ] : [];

  // Top-level admin only items (adam.jagger@zkteco.co.uk)
  const topAdminItems = user && user.email === 'adam.jagger@zkteco.co.uk' ? [
    {
      id: 'broadcast-email',
      title: 'Broadcast Email',
      subtitle: '(Send to All Users)',
      icon: Mail,
      color: 'from-rose-500 to-pink-600',
      hoverColor: 'hover:from-rose-600 hover:to-pink-700',
      navigate: 'broadcast-email'
    },
    {
      id: 'resource-manager',
      title: 'Resource Manager',
      subtitle: '(Videos & Media)',
      icon: Film,
      color: 'from-violet-500 to-purple-600',
      hoverColor: 'hover:from-violet-600 hover:to-purple-700',
      navigate: 'resource-manager'
    }
  ] : [];

  // Determine which items to show based on user role
  const dashboardItems = user && (user.role === 'admin' || user.role === 'zkteco_admin' || user.role === 'distributor_admin')
    ? [...baseDashboardItems, ...adminItems, ...sharedToolsItems, ...adminOnlyToolsItems, ...topAdminItems]
    : [...baseDashboardItems, ...sharedToolsItems];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-8">
      <div className="max-w-6xl mx-auto">
        {/* Top Right Buttons */}
        <div className="flex justify-end gap-2 mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/account-settings')}
            className="text-slate-700 hover:text-slate-900"
          >
            <Settings className="h-4 w-4 mr-2" />
            Account
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onLogout}
            className="text-slate-700 hover:text-slate-900"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>

        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <img src="/zkteco-logo.png" alt="ZKTeco" className="h-32 w-auto" />
          </div>
          <p className="text-lg text-slate-600">
            ZKTeco UK Solution Configurator
          </p>
          {user && (
            <p className="text-sm text-slate-500 mt-2">
              Welcome back, {user.first_name || user.email}
            </p>
          )}
        </div>

        {/* Dashboard Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {dashboardItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => navigate(`/${item.navigate}`)}
                className="group relative overflow-hidden"
              >
                <Card className="border-2 border-slate-200 hover:border-green-500 transition-all duration-300 h-48">
                  <CardContent className="p-0 h-full">
                    <div className={`h-full bg-gradient-to-br ${item.color} ${item.hoverColor} transition-all duration-300 flex flex-col items-center justify-center text-white relative overflow-hidden`}>
                      {/* Notification Badge */}
                      {item.badge && (
                        <div className="absolute top-4 right-4 z-20">
                          <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center text-white text-sm font-bold shadow-lg animate-pulse">
                            {item.badge}
                          </div>
                        </div>
                      )}
                      
                      {/* Decorative background pattern */}
                      <div className="absolute inset-0 opacity-10">
                        <div className="absolute top-0 left-0 w-32 h-32 border-4 border-white rounded-full -translate-x-16 -translate-y-16"></div>
                        <div className="absolute bottom-0 right-0 w-32 h-32 border-4 border-white rounded-full translate-x-16 translate-y-16"></div>
                      </div>
                      
                      {/* Icon */}
                      <div className="relative z-10 mb-4 transform group-hover:scale-110 transition-transform duration-300">
                        <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center border-2 border-white/50">
                          <Icon className="w-10 h-10" />
                        </div>
                      </div>
                      
                      {/* Text */}
                      <div className="relative z-10 text-center px-4">
                        <h3 className="text-xl font-bold">
                          {item.title}
                        </h3>
                        {item.subtitle && (
                          <p className="text-sm text-white/80 mt-1">
                            {item.subtitle}
                          </p>
                        )}
                      </div>
                      
                      {/* Hover effect */}
                      <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
                    </div>
                  </CardContent>
                </Card>
              </button>
            );
          })}

          {/* Feature Recommendation Card - Available to all users */}
          <button
            onClick={() => setShowFeatureForm(true)}
            className="group relative overflow-hidden"
          >
            <Card className="border-2 border-slate-200 hover:border-yellow-500 transition-all duration-300 h-48">
              <CardContent className="p-0 h-full">
                <div className="h-full bg-gradient-to-br from-yellow-500 to-amber-600 hover:from-yellow-600 hover:to-amber-700 transition-all duration-300 flex flex-col items-center justify-center text-white relative overflow-hidden">
                  {/* Decorative background pattern */}
                  <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 left-0 w-32 h-32 border-4 border-white rounded-full -translate-x-16 -translate-y-16"></div>
                    <div className="absolute bottom-0 right-0 w-32 h-32 border-4 border-white rounded-full translate-x-16 translate-y-16"></div>
                  </div>
                  
                  {/* Icon */}
                  <div className="relative z-10 mb-4 transform group-hover:scale-110 transition-transform duration-300">
                    <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center border-2 border-white/50">
                      <Lightbulb className="w-10 h-10" />
                    </div>
                  </div>
                  
                  {/* Text */}
                  <div className="relative z-10 text-center px-4">
                    <h3 className="text-xl font-bold">
                      Feature Request
                    </h3>
                    <p className="text-sm text-white/80 mt-1">Suggest a new feature</p>
                  </div>
                  
                  {/* Hover effect */}
                  <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
                </div>
              </CardContent>
            </Card>
          </button>
        </div>

        {/* Feature Recommendation Modal */}
        {showFeatureForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <Card className="max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                      <Lightbulb className="w-5 h-5 text-yellow-600" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-slate-800">Feature Request</h2>
                      <p className="text-sm text-slate-500">Tell us what you'd like to see</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowFeatureForm(false);
                      setFeatureError('');
                      setFeatureSuccess(false);
                    }}
                    className="h-8 w-8 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                {featureSuccess ? (
                  <Alert className="bg-green-50 border-green-200">
                    <AlertDescription className="text-green-800">
                      Thank you! Your suggestion has been submitted successfully.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <form onSubmit={handleFeatureSubmit} className="space-y-4">
                    {featureError && (
                      <Alert variant="destructive">
                        <AlertDescription>{featureError}</AlertDescription>
                      </Alert>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor="feature-name">Your Name *</Label>
                      <Input
                        id="feature-name"
                        value={featureForm.name}
                        onChange={(e) => setFeatureForm({...featureForm, name: e.target.value})}
                        placeholder="John Smith"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="feature-phone">Phone Number *</Label>
                      <Input
                        id="feature-phone"
                        value={featureForm.phone}
                        onChange={(e) => setFeatureForm({...featureForm, phone: e.target.value})}
                        placeholder="07123 456789"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="feature-company">Company *</Label>
                      <Input
                        id="feature-company"
                        value={featureForm.company}
                        onChange={(e) => setFeatureForm({...featureForm, company: e.target.value})}
                        placeholder="Your company name"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="feature-suggestion">Your Suggestion *</Label>
                      <Textarea
                        id="feature-suggestion"
                        value={featureForm.suggestion}
                        onChange={(e) => setFeatureForm({...featureForm, suggestion: e.target.value.slice(0, 500)})}
                        placeholder="Describe the feature you'd like to see..."
                        rows={4}
                        maxLength={500}
                        required
                      />
                      <p className="text-xs text-slate-500 text-right">
                        {featureForm.suggestion.length}/500 characters
                      </p>
                    </div>

                    <div className="flex gap-2 pt-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setShowFeatureForm(false);
                          setFeatureError('');
                        }}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={featureSubmitting}
                        className="flex-1 bg-yellow-600 hover:bg-yellow-700"
                      >
                        {featureSubmitting ? 'Submitting...' : 'Submit'}
                      </Button>
                    </div>
                  </form>
                )}
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
